import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Hash, Loader2 } from "lucide-react";
import api from "../lib/api";

export default function SearchBox({ variant = "nav", initialValue = "" }) {
  const nav = useNavigate();
  const [value, setValue] = useState(initialValue);
  const [suggest, setSuggest] = useState({ tags: [], titles: [] });
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const timer = useRef(null);

  const isHero = variant === "hero";

  const fetchSuggest = useCallback((qv) => {
    if (!qv.trim()) { setSuggest({ tags: [], titles: [] }); return; }
    setLoading(true);
    api.get("/search/suggest", { params: { q: qv } })
      .then(({ data }) => setSuggest(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => fetchSuggest(value), 220);
    return () => clearTimeout(timer.current);
  }, [value, fetchSuggest]);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const go = (path) => { setOpen(false); nav(path); };
  const submit = (e) => { e.preventDefault(); if (value.trim()) go(`/explore?q=${encodeURIComponent(value.trim())}`); };
  const hasSuggest = suggest.tags.length > 0 || suggest.titles.length > 0;

  return (
    <div ref={boxRef} className={`relative ${isHero ? "max-w-2xl" : "flex-1 max-w-xl"}`}>
      <form onSubmit={submit}>
        <div className="relative">
          <Search className={`absolute ${isHero ? "left-4" : "left-3"} top-1/2 -translate-y-1/2 text-[#52525B]`} size={isHero ? 20 : 16} />
          <input
            data-testid={isHero ? "hero-search-input" : "nav-search-input"}
            value={value}
            onChange={(e) => { setValue(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder={isHero ? "Search files by title, tag or kink…" : "Search files…"}
            className={`w-full bg-[#141414] border border-[#1C1C1F] text-white font-mono focus:outline-none focus:border-[#0066FF] transition-colors placeholder:text-[#52525B] rounded-xl ${
              isHero ? "text-base pl-12 pr-32 py-4" : "text-sm pl-10 pr-4 py-2.5"
            }`}
          />
          {isHero && (
            <button data-testid="hero-search-btn" type="submit" className="absolute right-0 top-0 h-full bg-[#0066FF] text-white font-heading uppercase tracking-widest text-sm px-6 hover:bg-[#0055CC] transition-colors">
              Search
            </button>
          )}
        </div>
      </form>

      {open && value.trim() && (
        <div data-testid="search-suggestions" className="absolute z-50 left-0 right-0 mt-1 bg-[#0A0A0A] border border-[#1C1C1F] shadow-[0_10px_40px_rgba(0,0,0,0.6)] max-h-80 overflow-y-auto">
          {loading && !hasSuggest && (
            <div className="flex items-center gap-2 px-4 py-3 text-[#52525B] font-mono text-xs"><Loader2 size={13} className="animate-spin" /> Searching…</div>
          )}
          {suggest.tags.length > 0 && (
            <div className="py-1">
              <div className="px-4 py-1 font-mono text-[9px] uppercase tracking-widest text-[#52525B]">Tags</div>
              {suggest.tags.map((t) => (
                <button key={t.tag} data-testid={`suggest-tag-${t.tag}`} onMouseDown={() => go(`/explore?tag=${t.tag}`)} className="w-full flex items-center justify-between px-4 py-2 text-left hover:bg-[#0A2540] transition-colors group">
                  <span className="flex items-center gap-2 text-[#FFFFFF] text-sm font-mono group-hover:text-[#0066FF]"><Hash size={13} className="text-[#0066FF]" />{t.tag}</span>
                  <span className="font-mono text-[10px] text-[#52525B]">{t.count}</span>
                </button>
              ))}
            </div>
          )}
          {suggest.titles.length > 0 && (
            <div className="py-1 border-t border-[#1C1C1F]">
              <div className="px-4 py-1 font-mono text-[9px] uppercase tracking-widest text-[#52525B]">Files</div>
              {suggest.titles.map((t, i) => (
                <button key={`${t}-${i}`} data-testid={`suggest-title-${i}`} onMouseDown={() => go(`/explore?q=${encodeURIComponent(t)}`)} className="w-full flex items-center gap-2 px-4 py-2 text-left hover:bg-[#0A2540] transition-colors text-[#FFFFFF] text-sm hover:text-white">
                  <Search size={13} className="text-[#52525B]" /> <span className="truncate">{t}</span>
                </button>
              ))}
            </div>
          )}
          {!loading && !hasSuggest && (
            <button onMouseDown={submit} className="w-full px-4 py-3 text-left text-[#52525B] font-mono text-xs hover:text-white transition-colors">
              No matches — search "{value}" anyway
            </button>
          )}
        </div>
      )}
    </div>
  );
}
